import { Text } from '@msc-b2c/shared-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PassengerI } from '@msc-b2c/interfaces/interfaces';
import { RefundPassengersProps } from '@msc-b2c/interfaces/types';
import {
  getActiveSession,
  replacePlaceholders,
  RootState,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { BookingStatusEnum, PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import { getCabinIdOfTheLoggedUser } from '../../../utils/myAreaUtils';
import {
  buildBookedExcursionPreferredLanguageMap,
  buildExcursionLanguageCodesForRefund,
} from '../../utils/refundPassengerBookingHelpers';
import {
  getCmsRelatedCodes,
  isAllInclusiveByCmsOrTypeCode,
  isAllInclusiveOrCombinedObsRefund,
} from '../../utils/allInclusiveCmsHelpers';
import { RefundPassengerRow } from './RefundPassengerRow';
import { getOrderedCabinsForPassengerDrawer } from '../../../utils/stateroomGenericUtils';
import { hasSameExcursionStartDate } from '../../../alreadypurchasedcontainer/alreadyPurchasedHelpers';
import styles from './passengers.module.scss';

const areCheckedStatesEqual = (
  a: Record<string, Record<string, boolean>>,
  b: Record<string, Record<string, boolean>>
) => {
  const aCabins = Object.keys(a);
  const bCabins = Object.keys(b);
  if (aCabins.length !== bCabins.length) return false;

  for (const cabinId of aCabins) {
    if (!b[cabinId]) return false;
    const aPassengers = Object.keys(a[cabinId]);
    const bPassengers = Object.keys(b[cabinId]);
    if (aPassengers.length !== bPassengers.length) return false;

    for (const passengerId of aPassengers) {
      if (a[cabinId][passengerId] !== b[cabinId][passengerId]) return false;
    }
  }

  return true;
};

function RefundPassengers({
  isDrawerOpen,
  onCheckedPassengersChange,
  cabinIdFilter,
  showMainTitle = true,
  showCabinTitle = true,
}: Readonly<RefundPassengersProps>) {
  const drawerAEMConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.prePaidCardProps
  );
  const drawerConfig = useSelector((state: RootState) => state.myAreaCommonConfig.drawerProps);
  const { selectedMyAreaExcursionCard, selectedMyAreaOBSCard, isLayerMyAreaExcursionOpen } =
    useSelector((state: RootState) => state.myAreaDrawers);
  const { obs, shopExcursion, allInclusiveCmsItems } = useSelector(
    (state: RootState) => state.myCruise
  );
  const drawerExcursionAEMConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerExcursionProps?.drawerExcursionType
  );
  const bookingNumber = getActiveSession().bookingNumber ?? '';
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });

  const activeCabinId = useMemo(() => getCabinIdOfTheLoggedUser(booking ?? undefined), [booking]);
  const sessionCruiseId = getActiveSession().cruiseId ?? '';

  const passengerData = useMemo(() => booking?.passengers ?? [], [booking]);
  const cabinsForPassengerList = useMemo(
    () =>
      getOrderedCabinsForPassengerDrawer(
        booking ?? undefined,
        sessionCruiseId || undefined,
        activeCabinId
      ),
    [booking, sessionCruiseId, activeCabinId]
  );

  const passengersPerCabin = useMemo(() => {
    const result: Record<string, PassengerI[]> = {};
    cabinsForPassengerList.forEach((cabin) => {
      const ids = new Set(
        (cabin.passengers ?? []).map((pax) =>
          typeof pax === 'object' && pax !== null ? pax.id : pax
        )
      );
      if (!cabin.id) {
        return;
      }
      result[cabin.id] = passengerData.filter((passenger) => passenger.id && ids.has(passenger.id));
    });
    return result;
  }, [cabinsForPassengerList, passengerData]);

  const activeCruiseIdFromSession = getActiveSession().cruiseId ?? '';
  const prepaidBookingsForCruise = useMemo(
    () =>
      activeCruiseIdFromSession
        ? (booking?.prepaid?.bookings ?? []).filter(
            (prepaidBooking) => prepaidBooking.cruise.id === activeCruiseIdFromSession
          )
        : booking?.prepaid?.bookings ?? [],
    [booking, activeCruiseIdFromSession]
  );
  const obsRefundItems = useMemo(() => {
    if (isLayerMyAreaExcursionOpen || !selectedMyAreaOBSCard) {
      return [];
    }

    const directRelatedCodes = [
      ...(selectedMyAreaOBSCard.prepaidItems?.serviceCodes?.adults ?? []),
      ...(selectedMyAreaOBSCard.prepaidItems?.serviceCodes?.minors ?? []),
      ...getCmsRelatedCodes(allInclusiveCmsItems, selectedMyAreaOBSCard.code),
    ].filter((code): code is string => Boolean(code));

    const relatedItems = directRelatedCodes
      .map((code) => obs?.onboardServices?.find((item) => item.code === code))
      .filter((item): item is NonNullable<typeof selectedMyAreaOBSCard> => Boolean(item));

    const reverseRelatedItems =
      (obs?.onboardServices ?? []).filter((item) => {
        if (item.code === selectedMyAreaOBSCard.code) {
          return false;
        }

        const itemCodes = [
          ...(item.prepaidItems?.serviceCodes?.adults ?? []),
          ...(item.prepaidItems?.serviceCodes?.minors ?? []),
        ];

        return itemCodes.includes(selectedMyAreaOBSCard.code ?? '');
      }) ?? [];

    return [selectedMyAreaOBSCard, ...relatedItems, ...reverseRelatedItems].filter(
      (item, index, array) => index === array.findIndex((candidate) => candidate.code === item.code)
    );
  }, [
    isLayerMyAreaExcursionOpen,
    selectedMyAreaOBSCard,
    obs?.onboardServices,
    allInclusiveCmsItems,
  ]);

  const obsCodesToCheck = useMemo(() => {
    if (isLayerMyAreaExcursionOpen || !selectedMyAreaOBSCard?.code) {
      return new Set<string>();
    }

    const selectedCode = selectedMyAreaOBSCard.code;
    const directRelatedCodes = [
      ...(selectedMyAreaOBSCard.prepaidItems?.serviceCodes?.adults ?? []),
      ...(selectedMyAreaOBSCard.prepaidItems?.serviceCodes?.minors ?? []),
      ...getCmsRelatedCodes(allInclusiveCmsItems, selectedCode),
    ].filter((code): code is string => Boolean(code));

    const reverseRelatedCodes = (obs?.onboardServices ?? [])
      .filter((item) => {
        if (item.code === selectedCode) {
          return false;
        }
        const relatedCodes = [
          ...(item.prepaidItems?.serviceCodes?.adults ?? []),
          ...(item.prepaidItems?.serviceCodes?.minors ?? []),
        ];
        return relatedCodes.includes(selectedCode);
      })
      .map((item) => item.code)
      .filter((code): code is string => Boolean(code));

    return new Set([selectedCode, ...directRelatedCodes, ...reverseRelatedCodes]);
  }, [
    isLayerMyAreaExcursionOpen,
    selectedMyAreaOBSCard,
    obs?.onboardServices,
    allInclusiveCmsItems,
  ]);

  const alreadyBookedPaxID = useMemo(() => {
    if (!booking || prepaidBookingsForCruise.length === 0) {
      return [];
    }

    if (isLayerMyAreaExcursionOpen && selectedMyAreaExcursionCard) {
      const bookedExcursions = prepaidBookingsForCruise
        .flatMap((prepaidBooking) => prepaidBooking.excursions ?? [])
        .filter(
          (item) =>
            item.code === selectedMyAreaExcursionCard.code &&
            hasSameExcursionStartDate(item.startDate, selectedMyAreaExcursionCard.startDate) &&
            item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
            (item.passenger?.id || item.cabin?.id)
        );

      return Array.from(
        new Set(
          bookedExcursions.flatMap((item) => {
            if (item.priceBasis === PriceBasisEnum.PER_PERSON && item.passenger?.id) {
              return [item.passenger.id];
            }
            if (item.priceBasis === PriceBasisEnum.PER_CABIN && item.cabin?.id) {
              return (passengersPerCabin[item.cabin.id] ?? [])
                .map((passenger) => passenger.id)
                .filter((id): id is string => Boolean(id));
            }
            return [];
          })
        )
      );
    }

    if (!isLayerMyAreaExcursionOpen && selectedMyAreaOBSCard) {
      const bookedObs = prepaidBookingsForCruise
        .flatMap((prepaidBooking) => prepaidBooking.onboardServices ?? [])
        .filter(
          (item) =>
            item.code &&
            obsCodesToCheck.has(item.code) &&
            item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
            (item.passenger?.id || item.cabin?.id)
        );

      return Array.from(
        new Set(
          bookedObs.flatMap((item) => {
            if (item.priceBasis === PriceBasisEnum.PER_PERSON && item.passenger?.id) {
              return [item.passenger.id];
            }
            if (item.priceBasis === PriceBasisEnum.PER_CABIN && item.cabin?.id) {
              return (passengersPerCabin[item.cabin.id] ?? [])
                .map((passenger) => passenger.id)
                .filter((id): id is string => Boolean(id));
            }
            return [];
          })
        )
      );
    }
    return [];
  }, [
    booking,
    prepaidBookingsForCruise,
    isLayerMyAreaExcursionOpen,
    selectedMyAreaExcursionCard,
    selectedMyAreaOBSCard,
    obsCodesToCheck,
    passengersPerCabin,
  ]);

  const [checkedPassengers, setCheckedPassengers] = useState<
    Record<string, Record<string, boolean>>
  >({});

  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }

    const initialCheckedState: Record<string, Record<string, boolean>> = {};
    Object.entries(passengersPerCabin).forEach(([cabinId, passengers]) => {
      initialCheckedState[cabinId] = {};
      passengers.forEach((passenger) => {
        if (passenger.id && alreadyBookedPaxID.includes(passenger.id)) {
          initialCheckedState[cabinId][passenger.id] = false;
        }
      });
    });
    setCheckedPassengers((prev) =>
      areCheckedStatesEqual(prev, initialCheckedState) ? prev : initialCheckedState
    );
  }, [passengersPerCabin, alreadyBookedPaxID, isDrawerOpen]);

  const onCheckedPassengersChangeRef = useRef(onCheckedPassengersChange);
  useEffect(() => {
    onCheckedPassengersChangeRef.current = onCheckedPassengersChange;
  }, [onCheckedPassengersChange]);

  useEffect(() => {
    onCheckedPassengersChangeRef.current(checkedPassengers);
  }, [checkedPassengers]);

  const passengerPriceMap = useMemo(() => {
    const priceMap = new Map<
      string,
      { afterDiscount?: string; beforeDiscount?: string; beforePromotionAndDiscount?: string }
    >();

    if (!prepaidBookingsForCruise.length) {
      return priceMap;
    }

    if (isLayerMyAreaExcursionOpen && selectedMyAreaExcursionCard) {
      prepaidBookingsForCruise
        .flatMap((prepaidBooking) => prepaidBooking.excursions ?? [])
        .filter(
          (item) =>
            item.code === selectedMyAreaExcursionCard.code &&
            hasSameExcursionStartDate(item.startDate, selectedMyAreaExcursionCard.startDate) &&
            item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
            item.priceBasis === PriceBasisEnum.PER_PERSON &&
            item.passenger?.id
        )
        .forEach((item) => {
          const passengerId = item.passenger?.id;
          if (!passengerId) return;
          const currentAfter = Number.parseFloat(priceMap.get(passengerId)?.afterDiscount ?? '0');
          const currentBefore = Number.parseFloat(priceMap.get(passengerId)?.beforeDiscount ?? '0');
          const itemAfter = Number.parseFloat(item.price?.total?.afterDiscount ?? '0');
          const itemBefore = Number.parseFloat(
            item.price?.total?.beforeDiscount ?? item.price?.total?.afterDiscount ?? '0'
          );
          priceMap.set(passengerId, {
            afterDiscount: (currentAfter + (Number.isNaN(itemAfter) ? 0 : itemAfter)).toString(),
            beforeDiscount: (
              currentBefore + (Number.isNaN(itemBefore) ? 0 : itemBefore)
            ).toString(),
            beforePromotionAndDiscount: (
              currentBefore + (Number.isNaN(itemBefore) ? 0 : itemBefore)
            ).toString(),
          });
        });
      return priceMap;
    }

    if (!isLayerMyAreaExcursionOpen && selectedMyAreaOBSCard) {
      prepaidBookingsForCruise
        .flatMap((prepaidBooking) => prepaidBooking.onboardServices ?? [])
        .filter(
          (item) =>
            item.code &&
            obsCodesToCheck.has(item.code) &&
            item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
            item.priceBasis === PriceBasisEnum.PER_PERSON &&
            item.passenger?.id
        )
        .forEach((item) => {
          const passengerId = item.passenger?.id;
          if (!passengerId) return;
          const currentAfter = Number.parseFloat(priceMap.get(passengerId)?.afterDiscount ?? '0');
          const currentBefore = Number.parseFloat(priceMap.get(passengerId)?.beforeDiscount ?? '0');
          const itemAfter = Number.parseFloat(item.price?.total?.afterDiscount ?? '0');
          const itemBefore = Number.parseFloat(
            item.price?.total?.beforeDiscount ?? item.price?.total?.afterDiscount ?? '0'
          );
          priceMap.set(passengerId, {
            afterDiscount: (currentAfter + (Number.isNaN(itemAfter) ? 0 : itemAfter)).toString(),
            beforeDiscount: (
              currentBefore + (Number.isNaN(itemBefore) ? 0 : itemBefore)
            ).toString(),
            beforePromotionAndDiscount: (
              currentBefore + (Number.isNaN(itemBefore) ? 0 : itemBefore)
            ).toString(),
          });
        });
    }

    return priceMap;
  }, [
    prepaidBookingsForCruise,
    isLayerMyAreaExcursionOpen,
    selectedMyAreaExcursionCard,
    selectedMyAreaOBSCard,
    obsCodesToCheck,
  ]);

  const hasRefundablePassengers = Object.values(passengersPerCabin).some((passengers) =>
    passengers.some((passenger) => passenger.id && alreadyBookedPaxID.includes(passenger.id))
  );
  const isAllInclusiveOrCombinedObsRefundFlow = isLayerMyAreaExcursionOpen
    ? isAllInclusiveByCmsOrTypeCode(allInclusiveCmsItems, selectedMyAreaExcursionCard?.code)
    : isAllInclusiveOrCombinedObsRefund(
        selectedMyAreaOBSCard,
        allInclusiveCmsItems,
        obs?.onboardServices
      ) || obsRefundItems.length > 1;
  const isPerCabinRefundItem = useMemo(() => {
    if (!prepaidBookingsForCruise.length) {
      return false;
    }

    if (isLayerMyAreaExcursionOpen && selectedMyAreaExcursionCard?.code) {
      return prepaidBookingsForCruise
        .flatMap((prepaidBooking) => prepaidBooking.excursions ?? [])
        .some(
          (item) =>
            item.code === selectedMyAreaExcursionCard.code &&
            hasSameExcursionStartDate(item.startDate, selectedMyAreaExcursionCard.startDate) &&
            item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
            item.priceBasis === PriceBasisEnum.PER_CABIN
        );
    }

    if (!isLayerMyAreaExcursionOpen && selectedMyAreaOBSCard?.code) {
      return prepaidBookingsForCruise
        .flatMap((prepaidBooking) => prepaidBooking.onboardServices ?? [])
        .some(
          (item) =>
            item.code &&
            obsCodesToCheck.has(item.code) &&
            item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
            item.priceBasis === PriceBasisEnum.PER_CABIN
        );
    }

    return false;
  }, [
    prepaidBookingsForCruise,
    isLayerMyAreaExcursionOpen,
    selectedMyAreaExcursionCard?.code,
    selectedMyAreaExcursionCard?.startDate,
    selectedMyAreaOBSCard,
    obsCodesToCheck,
  ]);

  const excursionLanguageList = useMemo(
    () =>
      buildExcursionLanguageCodesForRefund(
        isLayerMyAreaExcursionOpen,
        selectedMyAreaExcursionCard,
        shopExcursion
      ),
    [isLayerMyAreaExcursionOpen, selectedMyAreaExcursionCard, shopExcursion]
  );

  const clearRefundLanguageFieldErrors = useCallback(() => undefined, []);
  const noopRefundLanguageChange = useCallback(() => undefined, []);

  const bookedExcursionPreferredLangByCabinPassenger = useMemo(
    () =>
      buildBookedExcursionPreferredLanguageMap(
        isLayerMyAreaExcursionOpen,
        selectedMyAreaExcursionCard?.code,
        selectedMyAreaExcursionCard?.startDate,
        prepaidBookingsForCruise,
        passengersPerCabin,
        alreadyBookedPaxID
      ),
    [
      isLayerMyAreaExcursionOpen,
      selectedMyAreaExcursionCard?.code,
      selectedMyAreaExcursionCard?.startDate,
      prepaidBookingsForCruise,
      passengersPerCabin,
      alreadyBookedPaxID,
    ]
  );

  const setAllRefundablePassengersSelection = useCallback(
    (isSelected: boolean) => {
      const nextCheckedState: Record<string, Record<string, boolean>> = {};
      Object.entries(passengersPerCabin).forEach(([cid, passengersList]) => {
        nextCheckedState[cid] = {};
        passengersList.forEach((pax) => {
          if (pax.id && alreadyBookedPaxID.includes(pax.id)) {
            nextCheckedState[cid][pax.id] = isSelected;
          }
        });
      });
      setCheckedPassengers(nextCheckedState);
    },
    [passengersPerCabin, alreadyBookedPaxID]
  );
  const setCabinRefundablePassengersSelection = useCallback(
    (cabinId: string, isSelected: boolean) => {
      setCheckedPassengers((prev) => {
        const currentCabin = prev[cabinId] ?? {};
        const nextCabin = { ...currentCabin };
        (passengersPerCabin[cabinId] ?? []).forEach((pax) => {
          if (pax.id && alreadyBookedPaxID.includes(pax.id)) {
            nextCabin[pax.id] = isSelected;
          }
        });
        return {
          ...prev,
          [cabinId]: nextCabin,
        };
      });
    },
    [passengersPerCabin, alreadyBookedPaxID]
  );

  const handleRefundPassengerCheckboxChange = useCallback(
    (cabinKey: string, passengerId: string, currentChecked: boolean) => {
      if (isAllInclusiveOrCombinedObsRefundFlow) {
        setAllRefundablePassengersSelection(!currentChecked);
        return;
      }
      if (isPerCabinRefundItem) {
        setCabinRefundablePassengersSelection(cabinKey, !currentChecked);
        return;
      }
      setCheckedPassengers((prev) => ({
        ...prev,
        [cabinKey]: {
          ...prev[cabinKey],
          [passengerId]: !currentChecked,
        },
      }));
    },
    [
      isAllInclusiveOrCombinedObsRefundFlow,
      isPerCabinRefundItem,
      setAllRefundablePassengersSelection,
      setCabinRefundablePassengersSelection,
    ]
  );

  if (!hasRefundablePassengers) {
    return null;
  }

  if (cabinIdFilter) {
    const cabinList = passengersPerCabin[cabinIdFilter] ?? [];
    const hasRefundableInFilteredCabin = cabinList.some(
      (pax) => pax.id && alreadyBookedPaxID.includes(pax.id)
    );
    if (!hasRefundableInFilteredCabin) {
      return null;
    }
  }

  const isMultiCabin = Object.keys(passengersPerCabin).length > 1;
  const refundSectionLabel =
    drawerAEMConfig.refundEligiblePassengersLabel || drawerAEMConfig.alreadyBookedLabel;

  return (
    <div className={styles['passengers']}>
      {!isMultiCabin && showMainTitle && (
        <div className={styles['passengers__title']}>
          <Text text={refundSectionLabel} />
        </div>
      )}
      {Object.entries(passengersPerCabin).map(([cabinId, passengers], cabinIndex) => {
        if (cabinIdFilter && cabinId !== cabinIdFilter) {
          return null;
        }
        const bookedPassengers = passengers.filter(
          (passenger) => passenger.id && alreadyBookedPaxID.includes(passenger.id)
        );

        if (!bookedPassengers.length) {
          return null;
        }

        const disableCabin = activeCabinId ? activeCabinId !== cabinId : false;

        return (
          <div
            key={cabinId}
            className={`${styles['passengers__cabinGroup']} ${
              Object.keys(passengersPerCabin).length > 1 ? styles['multi-cabin'] : ''
            }`}
          >
            {Object.keys(passengersPerCabin).length > 1 && showCabinTitle && (
              <div className={styles['passengers__cabinGroup__title']}>
                <Text
                  text={replacePlaceholders(drawerConfig.stateroomLabel, {
                    id: (cabinIndex + 1).toString(),
                  })}
                />
              </div>
            )}
            {isMultiCabin && showMainTitle && (
              <div className={styles['passengers__sectionTitle']}>
                <Text text={refundSectionLabel} />
              </div>
            )}
            <div className={styles['passengers__checkboxes__container']}>
              {bookedPassengers.map((passenger) => {
                if (!passenger.id) {
                  return null;
                }
                const isChecked = checkedPassengers[cabinId]?.[passenger.id] ?? false;
                const passengerPriceData = passengerPriceMap.get(passenger.id);
                const preferredLangCode =
                  bookedExcursionPreferredLangByCabinPassenger.get(`${cabinId}:${passenger.id}`) ??
                  '';
                return (
                  <RefundPassengerRow
                    key={passenger.id}
                    passenger={passenger}
                    cabinId={cabinId}
                    isChecked={isChecked}
                    disableCabin={disableCabin}
                    passengerPriceData={passengerPriceData}
                    onCheckboxChange={() =>
                      handleRefundPassengerCheckboxChange(cabinId, passenger.id, isChecked)
                    }
                    preferredLangCode={preferredLangCode}
                    excursionLanguageList={excursionLanguageList}
                    isExcursionRefund={isLayerMyAreaExcursionOpen}
                    hasSelectedExcursion={Boolean(selectedMyAreaExcursionCard)}
                    selectLanguageLabel={drawerExcursionAEMConfig?.selectLanguageLabel ?? ''}
                    clearRefundLanguageFieldErrors={clearRefundLanguageFieldErrors}
                    noopRefundLanguageChange={noopRefundLanguageChange}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default RefundPassengers;
